import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OrgChart as D3OrgChart } from 'd3-org-chart';
import { MaterialSymbol, OrganizationEntity, PartnerOrganization, withStyles } from 'vopay';
import { OrgChart, changePage, toggleNode } from 'shared/orgChart';
import EntityCard from './EntityCard';
import { ENTITY_CARD_HEIGHT, ENTITY_CARD_WIDTH } from './entityCardSize';
import entityCreationsFor, { EntityCreation, EntityCreationPermissions } from './entityCreationsFor.helper';
import {
  buildOrganizationTree,
  findOrganizationNode,
  isSameOrganizationFilter,
  OrganizationFilter,
  OrganizationNode,
  OrganizationShowFilter,
} from './organizationTree';
import styles from './OrganizationChart.module.scss';

const cn = withStyles(styles);

const PREV_TREE_WIDTH = 73;
const PREV_TREE_HEIGHT = 32;

type OrganizationChartProps = {
  organization: PartnerOrganization;
  show: OrganizationShowFilter;
  /** The host's filters; the tree keeps the matches and the path to each. */
  filter: OrganizationFilter;
  displayLevel: number;
  /** "Show up to N": how many children of an expanded entity are drawn per page. */
  pageSize: number;
  /** An account or sub-account card was clicked; the organization's own card does nothing. */
  onAccountClick: (entity: OrganizationEntity) => void;
  /** A partner card was clicked. Absent, partner cards open nothing (a Viewer may not see a partner's keys). */
  onPartnerClick?: (entity: OrganizationEntity) => void;
  /** What the session may create where (spec 6.6); each card offers its share on hover. */
  creationPermissions: EntityCreationPermissions;
  /** A card's create button: the node is the parent the new entity goes under. */
  onCreate: (creation: EntityCreation, node: OrganizationNode) => void;
  /** Track Progress on an Application Pending card (spec 6.8). Absent, the button is not offered. */
  onTrackProgress?: (entity: OrganizationEntity) => void;
};

function isAccountNode(node: OrganizationNode) {
  return node.kind === 'Account' || node.kind === 'Sub-Account';
}

/**
 * The organization tree on the shared org chart engine (spec 6.4, 6.7): the engine draws and pages,
 * this component owns the tree — rebuilt from the data and filters, then edited by the cards' chips
 * and the load-more buttons — and renders every node kind.
 */
export default function OrganizationChart({
  organization,
  show,
  filter,
  displayLevel,
  pageSize,
  onAccountClick,
  onPartnerClick,
  creationPermissions,
  onCreate,
  onTrackProgress,
}: OrganizationChartProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Entities.OrganizationChart' });
  const { t: tCard } = useTranslation('translations', { keyPrefix: 'Entities.EntityCard' });
  const chartRef = useRef<D3OrgChart<OrganizationNode> | null>(null);

  // Read through refs so a parent re-render never re-draws the chart on its own.
  const onAccountClickRef = useRef(onAccountClick);
  onAccountClickRef.current = onAccountClick;
  const onPartnerClickRef = useRef(onPartnerClick);
  onPartnerClickRef.current = onPartnerClick;
  const onCreateRef = useRef(onCreate);
  onCreateRef.current = onCreate;
  const onTrackProgressRef = useRef(onTrackProgress);
  onTrackProgressRef.current = onTrackProgress;

  // Whether the two optional callbacks exist decides what a card renders, so those facts (not the
  // callbacks themselves) are what a re-draw depends on.
  const hasPartnerClick = onPartnerClick !== undefined;
  const hasTrackProgress = onTrackProgress !== undefined;

  // Display Levels only moves the engine's window over the tree the user has opened; it does not
  // rebuild the tree (the account portal's chart behaves the same), so a fresh build reads it through
  // a ref rather than depending on it.
  const displayLevelRef = useRef(displayLevel);
  displayLevelRef.current = displayLevel;

  // The tree on screen and the view it was built for, as of the last render: a refetch of the same
  // view (an entity was created) rebuilds from the new data but keeps what the user had opened; a
  // change of view starts over.
  const treeRef = useRef<OrganizationNode>(undefined);

  const viewRef = useRef<{ show: OrganizationShowFilter; filter: OrganizationFilter; pageSize: number }>(undefined);

  const baseTree = useMemo(() => {
    const view = viewRef.current;

    const isSameView =
      view !== undefined &&
      view.show === show &&
      isSameOrganizationFilter(view.filter, filter) &&
      view.pageSize === pageSize;

    return buildOrganizationTree(organization, {
      show,
      filter,
      pageSize,
      displayLevel: displayLevelRef.current,
      expansionFrom: isSameView ? treeRef.current : undefined,
    });
  }, [organization, show, filter, pageSize]);

  // The chips and load-more buttons edit the tree; a new base discards the edits, which is what a
  // rebuilt tree means — without an effect, so there is no stale frame.
  const [edited, setEdited] = useState<{ base: OrganizationNode; tree: OrganizationNode }>();
  const tree = edited?.base === baseTree ? edited.tree : baseTree;

  useEffect(() => {
    treeRef.current = tree;
    viewRef.current = { show, filter, pageSize };
  });

  // "Account · 26 client accounts · 3 sub-accounts": the type, then every non-zero count, in the
  // spec's order (6.5).
  const metaFor = useCallback(
    ({ kind, entity }: OrganizationNode) => {
      const counts = [
        entity.NumPartners > 0 && tCard('partners', { count: entity.NumPartners, defaultValue: '{{count}} partners' }),
        entity.NumAccounts > 0 && tCard('accounts', { count: entity.NumAccounts, defaultValue: '{{count}} accounts' }),
        entity.NumClientAccounts > 0 &&
          tCard('clientAccounts', { count: entity.NumClientAccounts, defaultValue: '{{count}} client accounts' }),
        entity.NumSubAccounts > 0 &&
          tCard('subAccounts', { count: entity.NumSubAccounts, defaultValue: '{{count}} sub-accounts' }),
      ];

      return [tCard(`Kind.${kind}`, { defaultValue: kind }), ...counts.filter(Boolean)].join(' · ');
    },
    [tCard],
  );

  // One render per tree edit: the engine re-draws whenever this element changes, so its callbacks
  // close over the tree they were built for rather than being recreated every parent render.
  return useMemo(() => {
    const updateTree = (next: OrganizationNode) => setEdited({ base: baseTree, tree: next });

    const toggleChildren = (node: OrganizationNode) =>
      updateTree(toggleNode(tree, { id: node.id, level: node.level, ancestorIDs: node.ancestorIDs }));

    return (
      <OrgChart
        tree={tree}
        pageSize={pageSize}
        displayLevel={displayLevel}
        chartRef={chartRef}
        nodeWidth={(node) => (node.nodeType === 'prevTree' ? PREV_TREE_WIDTH : ENTITY_CARD_WIDTH)}
        // Every card is the same height, and a load-more node takes it too, so its button sits level
        // with the cards beside it rather than at the top of the row.
        nodeHeight={(node) => (node.nodeType === 'prevTree' ? PREV_TREE_HEIGHT : ENTITY_CARD_HEIGHT)}
        childrenMargin={56}
        siblingsMargin={30}
        neighborMargin={30}
        isWheelZoomEnabled
        onNodeClick={(node) => {
          if (node.nodeType !== 'entity') return;

          if (isAccountNode(node)) onAccountClickRef.current(node.entity);
          if (node.kind === 'Partner') onPartnerClickRef.current?.(node.entity);
        }}
        renderComponent={(node) => {
          if (node.nodeType === 'entity') {
            return (
              <EntityCard
                node={node}
                meta={metaFor(node)}
                isClickable={isAccountNode(node) || (node.kind === 'Partner' && hasPartnerClick)}
                creations={entityCreationsFor(node, creationPermissions)}
                onCreate={(creation, parent) => onCreateRef.current(creation, parent)}
                onTrackProgress={
                  hasTrackProgress ? (tracked) => onTrackProgressRef.current?.(tracked.entity) : undefined
                }
                onToggleChildren={toggleChildren}
                totalBalances={node.kind === 'Organization' ? organization.Totals.Balances : undefined}
              />
            );
          }

          if (node.nodeType === 'prevTree') {
            // An expanded branch above the Display Levels window: the "…" stand-in opens it.
            return (
              <button
                type="button"
                className={cn('prevTree')}
                aria-label={t('showBranch', { name: node.entity.Name, defaultValue: 'Show {{name}}' })}
                onClick={() => toggleChildren(node)}
              >
                <MaterialSymbol icon="more_horiz" />
              </button>
            );
          }

          // Load-more nodes carry their first sibling's data, so the parent is looked up by id.
          const parentName = findOrganizationNode(tree, node.parentId)?.entity.Name ?? '';
          const count = Math.min(node.numChildren, pageSize);
          const isNext = node.nodeType === 'loadNext';

          return (
            <div className={cn('loadMoreContainer')}>
              <button
                type="button"
                className={cn('loadMore')}
                aria-label={
                  isNext
                    ? t('loadNextLabel', {
                        count,
                        parentName,
                        defaultValue: 'Show the next {{count}} under {{parentName}}',
                      })
                    : t('loadPreviousLabel', {
                        count,
                        parentName,
                        defaultValue: 'Show the previous {{count}} under {{parentName}}',
                      })
                }
                onClick={() => updateTree(changePage(tree, node.parentId, isNext ? 'next' : 'prev'))}
              >
                <MaterialSymbol icon="cached" />

                {isNext
                  ? t('loadNext', { count, defaultValue: 'Show next {{count}}' })
                  : t('loadPrevious', { count, defaultValue: 'Show previous {{count}}' })}
              </button>
            </div>
          );
        }}
      />
    );
  }, [
    tree,
    baseTree,
    pageSize,
    displayLevel,
    metaFor,
    organization.Organization.Name,
    organization.Totals.Balances,
    t,
    creationPermissions,
    hasPartnerClick,
    hasTrackProgress,
  ]);
}
